import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faExternalLinkAlt,
    faInfoCircle,
    faChevronRight,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import { storageHelper } from '../../Helpers/storageHelper';
import VideoViewerV2 from './VideoViewerV2';
import './VideoViewerV2.css';
import './AdsVideoViewer.css';

const AdsVideoViewer = ({
    videoUrl,
    poster,
    adTitle = "إعلان",
    adsBadge = "",
    adDescription = "",
    brandLogo = null,
    brandName = "",
    ctaText = "اعرف المزيد",
    ctaLink = "#",
    onCtaClick,
    onAdComplete,
    onAdSkip,
    onAdView,
    skipAfter = 5,
    autoPlay = true,
    showBranding = true,
    showCta = true,
    allowSkip = true,
    allowClose = false,
    onClose,
    className = "",
    style = {},
    overlay = false,
    position = "center",
    size = "large",
    useWatchedCache = false
}) => {
    const containerRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [currentTime, setCurrentTime] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [canSkip, setCanSkip] = useState(false);
    const [skipCountdown, setSkipCountdown] = useState(skipAfter);
    const [hasViewed, setHasViewed] = useState(false);
    const [shouldRender, setShouldRender] = useState(true);

    const getCacheKey = () => 'ad_watched_' + videoUrl.replace(/[^a-zA-Z0-9]/g, '');

    useEffect(() => {
        if (useWatchedCache && videoUrl) {
            const cacheKey = getCacheKey();
            const isWatched = storageHelper.getItem(cacheKey);
            if (isWatched === 'true') {
                setShouldRender(false);
                if (onClose) onClose();
            }
        }
    }, [useWatchedCache, videoUrl]);

    const markAsWatched = () => {
        if (useWatchedCache && videoUrl) {
            const cacheKey = getCacheKey();
            storageHelper.setItem(cacheKey, 'true', 3);
        }
    };

    const handleTimeUpdate = (time) => {
        setCurrentTime(time);
        if (!hasViewed && time >= 1) {
            setHasViewed(true);
            if (onAdView) onAdView();
        }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleEnded = () => {
        setIsPlaying(false);
        markAsWatched();
        if (onAdComplete) onAdComplete();
    };

    const handleSkip = () => {
        if (canSkip && allowSkip) {
            markAsWatched();
            if (onAdSkip) onAdSkip();
            if (onClose) onClose();
        }
    };

    const handleClose = () => {
        if (allowClose && onClose) {
            markAsWatched();
            onClose();
        }
    };

    const handleCtaClick = () => {
        markAsWatched();
        if (onCtaClick) onCtaClick();
        else if (ctaLink && ctaLink !== "#") window.open(ctaLink, '_blank');
    };

    useEffect(() => {
        if (isPlaying && !canSkip && skipAfter > 0) {
            const interval = setInterval(() => {
                setSkipCountdown((prev) => {
                    if (prev <= 1) {
                        setCanSkip(true);
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isPlaying, canSkip, skipAfter]);

    const getSizeClass = () => {
        switch (size) {
            case 'small': return 'ads-video-small';
            case 'medium': return 'ads-video-medium';
            case 'large': return 'ads-video-large';
            case 'fullscreen': return 'ads-video-fullscreen';
            default: return 'ads-video-large';
        }
    };

    const getPositionClass = () => {
        if (!overlay) return '';
        switch (position) {
            case 'top-right': return 'ads-position-top-right';
            case 'top-left': return 'ads-position-top-left';
            case 'bottom-right': return 'ads-position-bottom-right';
            case 'bottom-left': return 'ads-position-bottom-left';
            case 'center': return 'ads-position-center';
            default: return 'ads-position-center';
        }
    };

    if (!shouldRender) return null;

    return (
        <>
            {overlay && <div className="ads-video-backdrop" />}

            <div
                ref={containerRef}
                className={`video-viewer-v2-wrapper ads-video-viewer ${className} ${getSizeClass()} ${getPositionClass()} ${overlay ? 'ads-overlay-mode' : ''}`}
                style={style}
            >
                {adsBadge && (
                    <div className="ads-badge">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <span>{adsBadge}</span>
                    </div>
                )}

                {allowClose && (
                    <button type="button" className="ads-close-btn" onClick={handleClose} title="إغلاق">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}

                <VideoViewerV2
                    url={videoUrl}
                    poster={poster}
                    title={adTitle}
                    autoPlay={autoPlay}
                    muted={isMuted}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                    onTimeUpdate={handleTimeUpdate}
                />

                {showBranding && (brandLogo || brandName) && (
                    <div className="ads-branding">
                        {brandLogo && (
                            <div className="ads-brand-logo">
                                <img src={brandLogo} alt={brandName || ''} />
                            </div>
                        )}
                        {brandName && <span className="ads-brand-name">{brandName}</span>}
                    </div>
                )}

                {allowSkip && (
                    <button
                        type="button"
                        className={`ads-skip-btn ${canSkip ? 'can-skip' : 'countdown'}`}
                        onClick={handleSkip}
                        disabled={!canSkip}
                    >
                        {canSkip ? (
                            <>
                                <span className="ads-skip-text">تخطي الإعلان</span>
                                <FontAwesomeIcon icon={faChevronRight} />
                            </>
                        ) : (
                            <>
                                <span className="ads-skip-text">تخطي بعد {skipCountdown}s</span>
                                <FontAwesomeIcon icon={faClock} className="ads-skip-icon-wait" style={{ opacity: 0.5 }} />
                            </>
                        )}
                    </button>
                )}

                {showCta && ctaText && (
                    <div className="ads-cta-container">
                        <div className="ads-cta-content">
                            {adTitle && <h3 className="ads-cta-title">{adTitle}</h3>}
                            {adDescription && <p className="ads-cta-description">{adDescription}</p>}
                            <button type="button" className="ads-cta-button" onClick={handleCtaClick}>
                                {ctaText}
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AdsVideoViewer;
